import { Edit, Visibility } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, PhotoCard } from 'components'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, MichiganSOSBundleTitleApplicationDeliveryPhotoDto } from 'dtos'
import { useDialogState, useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import UploadReplaceTitleApplicationDeliveryPhotoDialog from './UploadReplaceTitleApplicationDeliveryPhotoDialog'
import ViewTitleApplicationDeliveryPhotoDialog from './ViewTitleApplicationDeliveryPhotoDialog'

interface MichiganSOSBundlesTitleApplicationDeliveryPhotoCardProps {
  michiganSOSBundle: MichiganSOSBundleDto
}

export default function MichiganSOSBundlesTitleApplicationDeliveryPhotoCard({
  michiganSOSBundle
}: MichiganSOSBundlesTitleApplicationDeliveryPhotoCardProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingMichiganSOSBundlesTitleApplicationDelivery: false,

    isSavingTitleApplicationDeliveryPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    michiganSOSBundlesTitleApplicationDeliveryViewDialogOpen: false,
    titleApplicationDeliveryPhoto: false
  })

  const [titleApplicationDeliveryPhoto, setTitleApplicationDeliveryPhoto] =
    useState<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>(new MichiganSOSBundleTitleApplicationDeliveryPhotoDto())

  useEffect(() => {
    if (michiganSOSBundle.michiganSOSBundleGuid) {
      setLoadingState('isGettingMichiganSOSBundlesTitleApplicationDelivery', true)
      controllers.michiganSoSBundles
        .getTitleApplicationDeliveryPhoto(michiganSOSBundle.michiganSOSBundleGuid)
        .then(res => {
          setTitleApplicationDeliveryPhoto(res.value)
        })
        .finally(() => {
          setLoadingState('isGettingMichiganSOSBundlesTitleApplicationDelivery', false)
        })
    }
  }, [michiganSOSBundle])

  return (
    <>
      {titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName && (
        <ViewTitleApplicationDeliveryPhotoDialog
          onClose={() => {
            setDialogState('michiganSOSBundlesTitleApplicationDeliveryViewDialogOpen', false)
          }}
          open={dialogState.michiganSOSBundlesTitleApplicationDeliveryViewDialogOpen}
          src={`/api/michigan-sos-bundles/${encodeURIComponent(
            michiganSOSBundle.michiganSOSBundleGuid!
          )}/title-application-delivery-photo/${encodeURIComponent(
            titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName
          )}`}
        />
      )}

      <UploadReplaceTitleApplicationDeliveryPhotoDialog
        initialValues={titleApplicationDeliveryPhoto}
        isLoading={loadingState.isSavingTitleApplicationDeliveryPhoto}
        michiganSoSBundleGuid={michiganSOSBundle.michiganSOSBundleGuid!}
        onClose={() => {
          setDialogState('titleApplicationDeliveryPhoto', false)
        }}
        onSave={values => {
          setLoadingState('isSavingTitleApplicationDeliveryPhoto', true)
          controllers.michiganSoSBundles
            .uploadReplaceTitleApplicationDeliveryPhoto(michiganSOSBundle.michiganSOSBundleGuid!, values)
            .then(res => {
              enqueueSnackbar('Title Application Delivery Photo Saved Successfully', { variant: 'success' })
              setTitleApplicationDeliveryPhoto(res.value)
              setDialogState('titleApplicationDeliveryPhoto', false)
            })
            .finally(() => {
              setLoadingState('isSavingTitleApplicationDeliveryPhoto', false)
            })
        }}
        open={dialogState.titleApplicationDeliveryPhoto}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Title Application Delivery Photo
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    disabled={!Boolean(titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName)}
                    endIcon={<Visibility />}
                    onClick={() => {
                      setDialogState('michiganSOSBundlesTitleApplicationDeliveryViewDialogOpen', true)
                    }}
                    variant='text'
                  >
                    VIEW SELECTED
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setDialogState('titleApplicationDeliveryPhoto', true)
                    }}
                    variant='text'
                  >
                    UPLOAD/REPLACE
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingMichiganSOSBundlesTitleApplicationDelivery && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12} container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
              <PhotoCard
                complete={Boolean(titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName)}
                label='Title Application Delivery'
                src={
                  titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName
                    ? `/api/michigan-sos-bundles/${encodeURIComponent(
                        michiganSOSBundle.michiganSOSBundleGuid!
                      )}/title-application-delivery-photo/${encodeURIComponent(
                        titleApplicationDeliveryPhoto.titleApplicationDeliveryPhotoFileName
                      )}`
                    : undefined
                }
              />
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**VIEW SELECTED Button:**

The “VIEW SELECTED” button is only available if the user has selected photos to view.

Clicking the “VIEW SELECTED” button will open the View Check In Photo Dialog.

**Title Application Delivery Photo:**

Displays the Title Application Delivery image.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the Title Application Delivery Photo image will select it.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
